import { useEffect, useState } from 'react';

import type { Channel, StreamChat } from 'stream-chat';
import type { TThread } from '@/services/chat';

import { createThrottle } from '@/utils/throttle';
import { toThread } from '@/utils/chat';
import { logger } from '@/services/logger';

type TThreadList = {
  threads: TThread[];
  channels: Channel[];
  isPending: boolean;
};

const CHANNEL_LIMIT = 30;

const REFRESH_EVENTS = [
  'message.new',
  'notification.message_new',
  'notification.added_to_channel',
  'notification.removed_from_channel',
  'notification.mark_read',
];

const REFRESH_WAIT_MS = 300;

const useThreads = (client: StreamChat | null): TThreadList => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isPending, setIsPending] = useState(true);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!client?.userID) {
      return undefined;
    }

    const throttled = createThrottle(() => {
      setVersion(previous => previous + 1);
    }, REFRESH_WAIT_MS);

    const listeners = REFRESH_EVENTS.map(name => client.on(name, throttled.run));

    return () => {
      throttled.cancel();
      listeners.forEach(listener => listener.unsubscribe());
    };
  }, [client]);

  useEffect(() => {
    if (!client?.userID) {
      return undefined;
    }

    let isActive = true;
    const staffID = client.userID;

    const load = async () => {
      try {
        const found = await client.queryChannels(
          { type: 'messaging', members: { $in: [staffID] } },
          [{ last_message_at: -1 }],
          { limit: CHANNEL_LIMIT, state: true, watch: true },
        );

        if (isActive) {
          setChannels(found);
          setIsPending(false);
        }
      } catch (error) {
        logger.error('useThreads', error);

        if (isActive) {
          setIsPending(false);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [client, version]);

  const staffID = client?.userID ?? '';
  const threads = channels.map(channel => toThread(channel, staffID));

  return { threads, channels, isPending };
};

export type {
  TThreadList,
};
export {
  useThreads,
};
